import urls from "../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../utils/service";

const base = urls.url_prod;

export type WorkflowStatus = "draft" | "published" | "archived";

export type AutomationNodeType =
  | "start"
  | "end"
  | "lead_capture"
  | "lead_source"
  | "stage_trigger"
  | "stage_action"
  | "multi_action"
  | "smart_routing"
  | "masked_call"
  | "send_whatsapp"
  | "send_email"
  | "send_sms"
  | "feedback"
  | "follow_up"
  | "opportunity"
  | "condition"
  | "ivr_call";

/** Normalize Mongo / API ids so navigation never becomes /edit/[object Object] */
export function toIdString(value: any): string {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    const s = String(value).trim();
    if (!s || s === "[object Object]" || s === "undefined" || s === "null") {
      return "";
    }
    return s;
  }
  if (typeof value === "object") {
    if (typeof (value as any).$oid === "string") return String((value as any).$oid);
    if (typeof (value as any).oid === "string") return String((value as any).oid);
    // BSON extended / driver shapes
    if (typeof (value as any).toHexString === "function") {
      try {
        return String((value as any).toHexString());
      } catch (_) {
        /* ignore */
      }
    }
    if ((value as any)._id && (value as any)._id !== value) {
      const nested = toIdString((value as any)._id);
      if (nested) return nested;
    }
    if ((value as any).id && (value as any).id !== value) {
      const nested = toIdString((value as any).id);
      if (nested) return nested;
    }
    // Empty leftover {} from bad ObjectId serialization
    if (Object.keys(value as object).length === 0) return "";
    const asStr =
      typeof (value as any).toString === "function"
        ? (value as any).toString()
        : "";
    if (
      asStr &&
      asStr !== "[object Object]" &&
      asStr !== "[object BSON]" &&
      /^[a-fA-F0-9]{24}$/.test(asStr)
    ) {
      return asStr;
    }
  }
  return "";
}

function normalizeDateValue(value: any): string | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "object" && typeof (value as any).$date !== "undefined") {
    value = (value as any).$date;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export type AutomationNode = {
  id: string;
  type: AutomationNodeType;
  position: { x: number; y: number };
  data: Record<string, any>;
};

export type AutomationEdge = {
  id: string;
  source: string;
  target: string;
  label?: string;
};

export type AutomationWorkflow = {
  _id?: string;
  organizationId: string;
  name: string;
  description?: string;
  status?: WorkflowStatus;
  triggerType?: string;
  nodes?: AutomationNode[];
  edges?: AutomationEdge[];
  settings?: Record<string, any>;
  scope?: "organization" | "global" | "project" | "form";
  scopeProjectIds?: string[];
  scopeFormIds?: string[];
  createdAt?: string;
  updatedAt?: string;
};

/** Normalize a workflow row from the API for table / navigation */
export function normalizeWorkflowRow(row: any): AutomationWorkflow {
  if (!row || typeof row !== "object") {
    return row;
  }
  const id = toIdString(row._id ?? row.id);
  return {
    ...row,
    _id: id || undefined,
    organizationId: toIdString(row.organizationId) || (row.organizationId as any),
    createdAt: normalizeDateValue(row.createdAt) || row.createdAt,
    updatedAt: normalizeDateValue(row.updatedAt) || row.updatedAt,
  };
}

export async function listAutomationWorkflows(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}) {
  const callParams = await getCallParams("GET");
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  if (params?.status) qs.set("status", params.status);
  const q = qs.toString();
  return makeCall(
    `${base}/automation-workflows${q ? `?${q}` : ""}`,
    callParams
  );
}

export async function getAutomationWorkflow(id: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/automation-workflows/${id}`, callParams);
}

export async function createAutomationWorkflow(body: Partial<AutomationWorkflow>) {
  const callParams = await getCallParams("POST", body);
  return makeCall(`${base}/automation-workflows`, callParams);
}

export async function updateAutomationWorkflow(
  id: string,
  body: Partial<AutomationWorkflow>
) {
  const callParams = await getCallParams("PUT", body);
  return makeCall(`${base}/automation-workflows/${id}`, callParams);
}

export async function publishAutomationWorkflow(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/automation-workflows/${id}/publish`, callParams);
}

export async function archiveAutomationWorkflow(id: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(`${base}/automation-workflows/${id}/archive`, callParams);
}
